import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    car_preferences: "",
    source_country: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");

    try {
      const response = await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error("Failed to submit");
      
      setStatus("success");
      setFormData({ name: "", phone: "", car_preferences: "", source_country: "" });
    } catch (err) {
      setError("Ошибка отправки. Попробуйте позже.");
      setStatus("idle");
    }
  };

  if (status === "success") {
    return (
      <section id="contact" className="py-24 bg-[#0A0A0A]" data-testid="contact-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="py-20"
          >
            <CheckCircle className="w-16 h-16 text-white mx-auto mb-6" strokeWidth={1} />
            <h2 className="text-3xl md:text-4xl text-white font-normal mb-4">
              Заявка отправлена
            </h2>
            <p className="text-white/50 text-lg">
              Мы свяжемся с вами в ближайшее время
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section 
      id="contact" 
      className="py-24 bg-[#0A0A0A]"
      data-testid="contact-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-white/40 text-xs uppercase tracking-[0.3em] mb-4">
              Связаться
            </p>
            <h2 className="text-4xl md:text-5xl text-white font-normal mb-6">
              Оставить заявку
            </h2>
            <p className="text-white/50 text-lg font-light mb-12 leading-relaxed">
              Заполните форму и мы свяжемся с вами для обсуждения деталей. 
              Консультация бесплатная.
            </p>

            <div className="space-y-6">
              <div>
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Телефон</p>
                <a href="tel:+375293376600" className="text-white text-xl font-light hover:text-white/70 transition-colors">
                  +375 29 337-66-00
                </a>
              </div>
              <div>
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Telegram</p>
                <a 
                  href="https://t.me/+375333376600" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white text-xl font-light hover:text-white/70 transition-colors"
                >
                  @sertbelinginiring
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <input
                  type="text"
                  placeholder="Ваше имя"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input-premium w-full"
                  data-testid="contact-name"
                />
              </div>
              
              <div>
                <input
                  type="tel"
                  placeholder="Телефон"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="input-premium w-full"
                  data-testid="contact-phone"
                />
              </div>

              <div>
                <input
                  type="text"
                  placeholder="Какой автомобиль интересует?"
                  value={formData.car_preferences}
                  onChange={(e) => setFormData({ ...formData, car_preferences: e.target.value })}
                  className="input-premium w-full"
                  data-testid="contact-car"
                />
              </div>

              <div>
                <select
                  value={formData.source_country}
                  onChange={(e) => setFormData({ ...formData, source_country: e.target.value })}
                  className="input-premium w-full appearance-none cursor-pointer"
                  data-testid="contact-country"
                >
                  <option value="">Откуда привезти?</option>
                  <option value="china">Китай</option>
                  <option value="korea">Корея</option>
                  <option value="usa">США</option>
                  <option value="any">Не знаю / Любая страна</option>
                </select>
              </div>

              {error && (
                <p className="text-red-400 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="btn-premium w-full flex items-center justify-center gap-3"
                data-testid="contact-submit"
              >
                {status === "loading" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" strokeWidth={1.5} />
                    Отправить заявку
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
